import { FilterDataPointMetadataValue, FilterDataPointString } from '@/api/generatedApi';
import { normalizeFolderPath } from '@/components/Search/utils';
import { FolderTreeNode } from './types';

export const generateExpandedItems = (nodes: FolderTreeNode[], level: number = 0): string[] => {
  const expanded: string[] = [];
  for (const [index, node] of nodes.entries()) {
    if (!node) continue;
    const safeFilterValue = node.filterDataPointString.filter.split(/[^a-zA-Z0-9]/).join('_');
    const itemId = `tree-${level}-${index}-${safeFilterValue}`;
    expanded.push(itemId);
    if (node.subFolders.length > 0) {
      expanded.push(...generateExpandedItems(node.subFolders, level + 1));
    }
  }
  return expanded;
};

export const getExpandedItemsForSearch = (
  nodes: FolderTreeNode[],
  searchQuery: string,
  level: number = 0
): string[] => {
  const expanded: string[] = [];
  for (const [index, node] of nodes.entries()) {
    if (!node) continue;
    const safeFilterValue = node.filterDataPointString.filter.split(/[^a-zA-Z0-9]/).join('_');
    const itemId = `tree-${level}-${index}-${safeFilterValue}`;
    if (searchQuery.trim() || node.subFolders.length > 0) {
      expanded.push(itemId, ...getExpandedItemsForSearch(node.subFolders, searchQuery, level + 1));
    }
  }
  return expanded;
};

export const findItemIdByFilter = (
  nodes: FolderTreeNode[],
  filter: string,
  path: number[] = []
): string | undefined => {
  for (const [index, node] of nodes.entries()) {
    const currentPath = [...path, index];
    const safeFilterValue = node.filterDataPointString.filter.split(/[^a-zA-Z0-9]/).join('_');
    const itemId = `tree-${currentPath.join('-')}-${safeFilterValue}`;
    if (node.filterDataPointString.filter === filter) {
      return itemId;
    }
    if (node.subFolders.length > 0) {
      const found = findItemIdByFilter(node.subFolders, filter, currentPath);
      if (found) return found;
    }
  }
  return undefined;
};

export const findNodeByFilter = (nodes: FolderTreeNode[], filter: string): FolderTreeNode | undefined => {
  for (const node of nodes) {
    if (node.filterDataPointString.filter === filter) {
      return node;
    }
    if (node.subFolders.length > 0) {
      const found = findNodeByFilter(node.subFolders, filter);
      if (found) return found;
    }
  }
  return undefined;
};

export const buildSubtreeFilterValuesIndex = (nodes: FolderTreeNode[]): Map<string, string[]> => {
  const index = new Map<string, string[]>();
  const collect = (node: FolderTreeNode): string[] => {
    const values = [node.filterDataPointString.filter, ...node.subFolders.flatMap(child => collect(child))];
    index.set(node.filterDataPointString.filter, values);
    return values;
  };
  for (const node of nodes) {
    collect(node);
  }
  return index;
};

export const buildFolderTree = (
  filterData: (FilterDataPointString | FilterDataPointMetadataValue)[],
  filterName?: string,
  recursive?: boolean
): FolderTreeNode[] => {
  const isFolderFilter = filterName === 'Folder';

  if (isFolderFilter || recursive) {
    const treeMap = new Map<string, FolderTreeNode>();
    const rootNodes: FolderTreeNode[] = [];

    const stringFilters = filterData.filter((item): item is FilterDataPointString => typeof item.filter === 'string');

    const sortedFolders = [...stringFilters].sort((a, b) => a.filter.localeCompare(b.filter));

    for (const folder of sortedFolders) {
      const pathParts = folder.filter.split('/').filter((part: string) => part.length > 0);

      if (pathParts.length === 0) continue;

      let currentPath = '';
      let parentNode: FolderTreeNode | undefined = undefined;

      for (const part of pathParts) {
        currentPath = currentPath ? `${currentPath}/${part}` : part;

        if (!treeMap.has(currentPath)) {
          const filterPath = isFolderFilter ? normalizeFolderPath(currentPath) : currentPath;
          const node: FolderTreeNode = {
            filterDataPointString: {
              filter: filterPath,
              count: folder.count,
            },
            filterLabel: part,
            filterLabelLower: part.toLowerCase(),
            filterPathLower: filterPath.toLowerCase(),
            subFolders: [],
          };

          treeMap.set(currentPath, node);

          if (parentNode) {
            parentNode.subFolders.push(node);
          } else {
            rootNodes.push(node);
          }
        }

        parentNode = treeMap.get(currentPath)!;
      }
    }

    return rootNodes;
  } else {
    return filterData.map(item => {
      const filterValue = typeof item.filter === 'string' ? item.filter : String(item.filter);
      const filterPath = isFolderFilter ? normalizeFolderPath(filterValue) : filterValue;

      return {
        filterDataPointString: {
          filter: filterPath,
          count: item.count,
        },
        filterLabel: filterValue,
        filterLabelLower: filterValue.toLowerCase(),
        filterPathLower: filterPath.toLowerCase(),
        subFolders: [],
      };
    });
  }
};
